/**
 * Enhancement Dashboard Page
 * Status of all 10 enhancements
 */

import React, { useEffect, useState } from 'react';
import '../styles/validation.css';

const API_BASE = 'https://trading-api-1075463475276.us-central1.run.app/api/trading-validation';

const EnhancementDashboard: React.FC = () => {
  const [enhancements, setEnhancements] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEnhancements = async () => {
      try {
        const res = await fetch(`${API_BASE}/enhancements`);
        const data = await res.json();
        setEnhancements(data.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching enhancements:', error);
        setLoading(false);
      }
    };

    fetchEnhancements();
    const interval = setInterval(fetchEnhancements, 300000); // 5 min
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="validation-container"><div className="loading">Loading enhancements...</div></div>;

  const enhancementsList = [
    { key: '1_volatility_sizing', num: 1 },
    { key: '2_regime_detection', num: 2 },
    { key: '3_drawdown_recovery', num: 3 },
    { key: '4_trade_reconciliation', num: 4 },
    { key: '5_correlation_analysis', num: 5 },
    { key: '6_trade_clustering', num: 6 },
    { key: '7_health_monitoring', num: 7 },
    { key: '8_crypto_opportunities', num: 8 },
    { key: '9_ensemble_predictions', num: 9 },
    { key: '10_equity_analytics', num: 10 },
  ];

  return (
    <div className="validation-container">
      <div className="validation-header">
        <h1>Enhancement Dashboard</h1>
        <p className="subtitle">Status of all 10 enhancements</p>
      </div>

      {enhancements && (
        <div className="enhancements-grid-full">
          {enhancementsList.map(({ key, num }) => {
            const enh = enhancements[key];
            if (!enh) return null;

            return (
              <div key={key} className="enhancement-card">
                <div className="enhancement-header">
                  <div className="enhancement-number">#{num}</div>
                  <div className="enhancement-title">{enh.name}</div>
                  <div className={`enhancement-status ${enh.active ? 'active' : 'inactive'}`}>
                    {enh.active ? '✓ Active' : '✗ Inactive'}
                  </div>
                </div>

                <div className="enhancement-effectiveness">
                  <span className="label">Effectiveness:</span>
                  <span className={`badge ${enh.effectiveness?.toLowerCase()}`}>
                    {enh.effectiveness}
                  </span>
                </div>

                <div className="enhancement-metrics">
                  {Object.entries(enh).map(([metricKey, metricValue]: any) => {
                    if (['name', 'active', 'effectiveness'].includes(metricKey)) return null;

                    return (
                      <div key={metricKey} className="metric-row">
                        <span className="metric-label">{metricKey.replace(/_/g, ' ')}:</span>
                        <span className="metric-value">
                          {typeof metricValue === 'number' ? metricValue.toFixed(2) : String(metricValue)}
                        </span>
                      </div>
                    );
                  })}
                </div>

                <div className="enhancement-recommendation">
                  {num === 1 && 'Adjusting position sizes based on volatility'}
                  {num === 2 && 'Detecting market regime changes'}
                  {num === 3 && 'Recovery mode ready if needed'}
                  {num === 4 && 'Monitoring execution quality'}
                  {num === 5 && 'Blocking correlated entries'}
                  {num === 6 && 'Analyzing profitable clusters'}
                  {num === 7 && 'Performing health checks'}
                  {num === 8 && 'Scanning crypto opportunities'}
                  {num === 9 && 'Ensemble model voting active'}
                  {num === 10 && 'Validating system robustness'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default EnhancementDashboard;
